import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Loader2,
  PanelLeftClose,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  Trash2,
  MapPinned,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useAuth, useSession } from "@/lib/useAuth";
import { conversationsApi, ApiError, type ConversationOut } from "@/lib/api";
import { useBusyConvIds } from "@/lib/chatStreams";
import {
  conversationRecency,
  formatConversationTime,
  formatTs,
  type ConversationRecency,
} from "@/lib/format";
import { SidebarProvider } from "@/lib/sidebar";
import { ToastContainer } from "./ToastContainer";
import { showConfirm, showToast } from "@/lib/toast";
import { EVENT_CONVERSATIONS_CHANGED } from "@/lib/workflowConstants";

const SIDEBAR_COLLAPSED_KEY = "liando.sidebarCollapsed";
const SIDEBAR_SECTION_RECENT_KEY = "liando.sidebarSection.recentCollapsed";
const SIDEBAR_SECTION_MORE_KEY = "liando.sidebarSection.moreCollapsed";

const CONVERSATION_RECENCY_GROUPS: Array<{
  key: ConversationRecency;
  label: string;
}> = [
  { key: "today", label: "今天" },
  { key: "threeDays", label: "近三天" },
  { key: "week", label: "近一周" },
  { key: "older", label: "更早" },
];

function groupConversations(conversations: ConversationOut[]): Array<{
  key: ConversationRecency;
  label: string;
  conversations: ConversationOut[];
}> {
  const grouped = new Map<ConversationRecency, ConversationOut[]>();
  for (const conversation of conversations) {
    const key = conversationRecency(conversation.last_message_at);
    const items = grouped.get(key) ?? [];
    items.push(conversation);
    grouped.set(key, items);
  }
  return CONVERSATION_RECENCY_GROUPS.flatMap((group) => {
    const items = grouped.get(group.key);
    return items?.length ? [{ ...group, conversations: items }] : [];
  });
}

function readStoredBoolean(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeStoredBoolean(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function AppShell(): JSX.Element {
  const session = useSession();
  const { logout } = useAuth();
  const token = session.token;
  const navigate = useNavigate();
  const location = useLocation();
  const [convs, setConvs] = useState<ConversationOut[]>([]);
  const [loading, setLoading] = useState(false);
  const busyConvIds = useBusyConvIds();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return readStoredBoolean(SIDEBAR_COLLAPSED_KEY);
  });
  const [recentCollapsed, setRecentCollapsed] = useState<boolean>(() =>
    readStoredBoolean(SIDEBAR_SECTION_RECENT_KEY),
  );
  const [moreCollapsed, setMoreCollapsed] = useState<boolean>(() =>
    readStoredBoolean(SIDEBAR_SECTION_MORE_KEY),
  );

  const toggleSidebar = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      writeStoredBoolean(SIDEBAR_COLLAPSED_KEY, next);
      return next;
    });
  };

  const toggleRecentSection = (): void => {
    setRecentCollapsed((prev) => {
      const next = !prev;
      writeStoredBoolean(SIDEBAR_SECTION_RECENT_KEY, next);
      return next;
    });
  };


  const toggleMoreSection = (): void => {
    setMoreCollapsed((prev) => {
      const next = !prev;
      writeStoredBoolean(SIDEBAR_SECTION_MORE_KEY, next);
      return next;
    });
  };

  const refresh = async (): Promise<void> => {
    if (!token) return;
    setLoading(true);
    try {
      const convList = await conversationsApi.list(token);
      // 对话功能已移除：侧栏只列地块数据查询记录，历史聊天会话不再展示。
      setConvs(
        convList.conversations.filter((c) => c.conversation_type === "land_research"),
      );
    } catch (e) {
      console.error("list land queries failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname]);

  const hasRunningLandReport = convs.some(
    (conversation) =>
      conversation.conversation_type === "land_research" &&
      conversation.land_status === "running",
  );

  useEffect(() => {
    if (!hasRunningLandReport) return;
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRunningLandReport, token]);

  // External refresh triggers keep the sidebar in sync when the current route
  // stays the same but background data changes.
  useEffect(() => {
    const onChange = (): void => {
      void refresh();
    };
    window.addEventListener(EVENT_CONVERSATIONS_CHANGED, onChange);
    return () => {
      window.removeEventListener(EVENT_CONVERSATIONS_CHANGED, onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate("/login", { replace: true });
  };


  const renameConversation = async (convId: string, newTitle: string): Promise<void> => {
    if (!token) return;
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    try {
      const updated = await conversationsApi.rename(token, convId, trimmed);
      // optimistic patch
      setConvs((prev) =>
        prev.map((c) => (c.conv_id === convId ? { ...c, title: updated.title } : c)),
      );
    } catch (e) {
      console.error("rename failed", e);
      if (e instanceof ApiError) showToast(e.message, "error");
    }
  };

  const deleteConversation = async (convId: string): Promise<void> => {
    if (!token) return;
    if (
      !(await showConfirm("确认删除这条查询记录? 从列表移除, 后端仍可恢复。", {
        yesLabel: "删除",
        noLabel: "取消",
        tone: "danger",
      }))
    )
      return;
    try {
      const remainingConvs = convs.filter((c) => c.conv_id !== convId);
      await conversationsApi.archive(token, convId);
      setConvs(remainingConvs);
      // If we were viewing this run, go back to the query form.
      if (location.pathname.startsWith(`/land/${convId}`)) {
        navigate("/land", { replace: true });
      }
    } catch (e) {
      console.error("delete failed", e);
      if (e instanceof ApiError) showToast(e.message, "error");
    }
  };


  const initial = session.username?.[0]?.toUpperCase() ?? "?";
  const conversationGroups = groupConversations(convs);

  return (
    <div className="relative flex h-screen bg-canvas text-zinc-900">
      {/* Mobile Backdrop */}
      {!collapsed && (
        <div
          className="fixed inset-0 z-40 bg-zinc-900/10 backdrop-blur-[2px] transition-opacity lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden border-r border-cardline bg-canvas transition-all duration-300 ease-in-out lg:static lg:z-0",
          collapsed ? "-translate-x-full w-0 border-r-0" : "translate-x-0 w-72",
          "lg:translate-x-0 lg:w-72",
          collapsed && "lg:w-0",
        )}
      >
        {/* Header row: "+ 新查询" + collapse toggle */}
        <div className="flex items-center gap-1 px-3 pt-4">
          <button
            type="button"
            onClick={() => navigate("/land")}
            className={cn(
              "flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-[13px]",
              "text-zinc-700 transition-colors hover:bg-zinc-100",
            )}
          >
            <Plus className="h-4 w-4 text-zinc-500" />
            <span className="font-medium">新查询</span>
          </button>
          <button
            type="button"
            onClick={toggleSidebar}
            className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
            title="收起侧栏"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        <SidebarSectionHeader
          className="mt-5"
          label="查询记录"
          count={convs.length}
          collapsed={recentCollapsed}
          onToggle={toggleRecentSection}
        />
        {!recentCollapsed ? (
          <nav className="flex-1 overflow-y-auto px-2 pb-2">
            {loading && convs.length === 0 ? (
              <div className="space-y-1 px-3 pt-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-6 w-full animate-pulse rounded bg-zinc-100" />
                ))}
              </div>
            ) : convs.length === 0 ? (
              <div className="px-3 py-1.5 text-[12px] text-zinc-400">暂无查询记录</div>
            ) : (
              <div className="space-y-2">
                {conversationGroups.map((group) => (
                  <section key={group.key}>
                    <div className="px-3 pb-1 pt-1 text-[10px] font-medium text-zinc-400">
                      {group.label}
                    </div>
                    <ul className="space-y-0.5">
                      {group.conversations.map((c) => {
                        const title = c.title || "未命名查询";
                        const isBusy =
                          busyConvIds.has(c.conv_id) || c.land_status === "running";
                        const conversationPath =
                          c.land_status === "done"
                            ? `/land/${c.conv_id}/data`
                            : `/land/${c.conv_id}`;
                        return (
                          <li key={c.conv_id}>
                            <ConvNavItem
                              convId={c.conv_id}
                              to={conversationPath}
                              title={title}
                              createdAt={c.created_at}
                              lastMessageAt={c.last_message_at}
                              isBusy={isBusy}
                              landStatus={c.land_status}
                              onRename={(newTitle) => void renameConversation(c.conv_id, newTitle)}
                              onDelete={() => void deleteConversation(c.conv_id)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </nav>
        ) : null}


        {/* ---- More section: per-user pages + admin-only block ---- */}
        <div className="border-t border-zinc-200/60 pb-2 pt-2">
          <SidebarSectionHeader
            label="更多"
            collapsed={moreCollapsed}
            onToggle={toggleMoreSection}
          />
          {!moreCollapsed ? (
            <div className="px-2">
              <AdminNavItem to="/land" label="地块数据" end />
              {session.role === "admin" ? (
                <>
                  <AdminNavItem to="/admin/users" label="用户管理" />
                  <AdminNavItem to="/admin/memory" label="长记忆中心" />
                  <AdminNavItem to="/admin/audit" label="审计日志" />
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ---- User identity footer ---- */}
        <div className="border-t border-zinc-200/60 px-3 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-900 text-[11px] font-medium text-white">
              {initial}
            </div>
            <div className="min-w-0 flex-1 text-[13px]">
              <div className="flex items-center gap-1.5 truncate text-zinc-800">
                <span className="truncate font-medium">{session.username}</span>
                {session.role === "admin" ? (
                  <span className="text-[11px] font-normal text-zinc-400">admin</span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              title="退出登录"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="relative flex flex-1 flex-col overflow-hidden bg-canvas">
        <SidebarProvider value={{ collapsed, toggle: toggleSidebar }}>
          <Outlet />
        </SidebarProvider>
      </main>

      <ToastContainer />
    </div>
  );
}

// Small group header consistent across sections.
function SidebarSectionHeader({
  label,
  className,
  count,
  collapsed,
  onToggle,
  icon,
}: {
  label: string;
  className?: string;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <div className={cn("px-5 pb-1.5 pt-1", className)}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between gap-2 text-[11px] font-medium tracking-wider text-zinc-400 transition-colors hover:text-zinc-700"
      >
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          {icon}
          <span>{label}</span>
          {typeof count === "number" ? (
            <span className="text-zinc-300 normal-case tracking-normal">{count}</span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 flex-shrink-0 transition-transform duration-200",
            collapsed && "-rotate-90",
          )}
        />
      </button>
    </div>
  );
}

// Sidebar conversation row — dot indicator becomes a spinner while busy.
// Hover surfaces a 3-dot menu with rename / delete actions.
function ConvNavItem({
  convId,
  to,
  title,
  createdAt,
  lastMessageAt,
  isBusy,
  landStatus,
  onRename,
  onDelete,
}: {
  convId: string;
  to: string;
  title: string;
  createdAt: number;
  lastMessageAt: number;
  isBusy: boolean;
  landStatus: ConversationOut["land_status"];
  onRename: (newTitle: string) => void;
  onDelete: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus & select-all when entering edit mode
  useEffect(() => {
    if (editing) {
      setDraft(title);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing, title]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const commit = (): void => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) onRename(trimmed);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2.5 rounded-md bg-zinc-100 px-3 py-1.5 text-[13px]">
        <span className="inline-block h-[5px] w-[5px] flex-shrink-0 rounded-full bg-zinc-800" />
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          maxLength={200}
          className="min-w-0 flex-1 bg-transparent text-[13px] text-zinc-900 focus:outline-none"
        />
      </div>
    );
  }

  return (
    <div className="group relative">
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] transition-colors",
            isActive
              ? "bg-brand-100/70 text-brand-900"
              : "text-zinc-600 hover:bg-brand-50 hover:text-zinc-800",
          )
        }
      >
        {({ isActive }) => (
          <>
            {isBusy ? (
              <Loader2
                className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-emerald-500"
                aria-label="正在工作"
              />
            ) : landStatus === "done" ? (
              <CheckCircle2
                className="h-3.5 w-3.5 flex-shrink-0 text-good"
                aria-label="数据已完成"
              />
            ) : landStatus === "pending" ? (
              <MapPinned
                className="h-3.5 w-3.5 flex-shrink-0 text-brand-500"
                aria-label="地块数据"
              />
            ) : (
              <span
                className={cn(
                  "inline-block h-[5px] w-[5px] flex-shrink-0 rounded-full transition-colors",
                  isActive ? "bg-brand-500" : "bg-zinc-300 group-hover:bg-brand-400",
                )}
              />
            )}
            <span className="min-w-0 flex-1 truncate">{title}</span>
            {landStatus ? (
              <span
                className={cn(
                  "shrink-0 pr-5 text-[10px]",
                  landStatus === "running"
                    ? "text-emerald-600"
                    : landStatus === "done"
                      ? "text-good"
                      : "text-zinc-400",
                )}
              >
                {landStatus === "running"
                  ? "搜索中"
                  : landStatus === "done"
                    ? "已完成"
                    : "待继续"}
              </span>
            ) : (
            <time
              dateTime={new Date(lastMessageAt * 1000).toISOString()}
              title={`创建：${formatTs(createdAt)}\n最近消息：${formatTs(lastMessageAt)}`}
              className="shrink-0 pr-5 text-[10px] text-zinc-400 transition-opacity group-hover:opacity-0"
            >
              {formatConversationTime(lastMessageAt)}
            </time>
            )}
          </>
        )}
      </NavLink>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        className={cn(
          "absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 transition-opacity",
          "opacity-0 group-hover:opacity-100 hover:bg-zinc-200 hover:text-zinc-700",
          menuOpen && "opacity-100",
        )}
        title="更多操作"
        aria-label={`更多操作 ${convId}`}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {menuOpen ? (
        <div
          ref={menuRef}
          className="absolute right-1 top-full z-20 mt-1 w-32 overflow-hidden rounded-md border border-zinc-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setEditing(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-zinc-700 hover:bg-zinc-50"
          >
            <Pencil className="h-3 w-3 text-zinc-400" />
            重命名
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onDelete();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3" />
            删除
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AdminNavItem({
  to,
  label,
  end,
}: {
  to: string;
  label: string;
  end?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] transition-colors",
          isActive
            ? "bg-zinc-100 text-zinc-900"
            : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-800",
        )
      }
    >
      <span className="inline-block h-[5px] w-[5px] flex-shrink-0 rounded-full bg-zinc-300" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

